<template>
    <div>
        <div v-if="isLoading" class="flex flex-col justify-center items-center h-40">
            <div class="w-full flex justify-center items-center">
                Please wait while we gather consent data.
            </div>
            <div class="w-12 h-12 py-8">
                <LoadingSpinner />
            </div>
        </div>
        <form v-else-if="visibleConsentForm" @submit.prevent="submitConsent" action="">
            <p class="pb-4">Please read the following carefully and indicate your consent by digitally signing below.</p>
            <div v-if="expandedConsentAgreement" @click="collapseConsentAgreement" class="text-sm h-[300px] relative overflow-auto px-4 pb-4 rounded-md border border-gray-300 bg-white cursor-pointer">
                <div class="sticky top-4 right-0 w-full flex justify-end">
                    <chevron-up-mini-s-v-g />
                </div>
                <h2 class="pb-3 font-bold text-gray-800">Photo Submission & Usage Agreement</h2>
                <p>By submitting your professional headshot, you agree to the following terms and conditions regarding its use by Shedd Capital, LLC:</p>
                <ul>
                    <li class="py-2">
                        <div class="font-bold text-gray-800">Grant of Rights:</div> 
                        <div>You grant Shedd Capital, LLC a non-exclusive, perpetual, worldwide, royalty-free license to use, reproduce, distribute, display, and create derivative works from your submitted photographs.</div>
                    </li>
                    <li class="py-2">
                        <div class="font-bold text-gray-800">Purpose of Use:</div> 
                        <div>Any photograph you submit may be used on Shedd Capital, LLC's official website, social media channels, marketing materials (e.g., brochures, presentations, advertisements), internal communications, and other promotional content. This includes, but is not limited to, "Alumni Spotlights," "Team Pages," and recruitment materials.</div>
                    </li>
                    <li class="py-2">
                        <div class="font-bold text-gray-800">No Commercial Gain (for you):</div> 
                        <div>You understand and agree that you will not receive any financial compensation for the use of your photographs.</div>
                    </li>
                    <li class="py-2">
                        <div class="font-bold text-gray-800">Right to Edit/Crop:</div> 
                        <div>You acknowledge that Shedd Capital, LLC may, at its discretion, crop, resize, or make minor edits to your photographs to fit various layouts and design requirements, while maintaining the professional integrity of the image.</div>
                    </li>
                    <li class="py-2">
                        <div class="font-bold text-gray-800">Withdrawal of Consent:</div> 
                        <div>While we appreciate your contribution, you may withdraw your consent for future use of your photographs at any time by withdrawing it from your user profile, or by emailing us at <a href="mailto:tech@sheddcapital.com" target="_blank" class="text-indigo-500 hover:underline">tech@sheddcapital.com</a>. Please note that it may take some time to remove your photographs from all distributed materials, especially those already printed or widely disseminated. However, we will make every reasonable effort to cease future use promptly.</div>
                    </li>
                    <li class="py-2">
                        <div class="font-bold text-gray-800">Limitation of Liability (Third Parties):</div> 
                        <div>You acknowledge that the withdrawal of consent applies only to the future actions and materials produced by Shedd Capital, LLC. You understand and agree that if an image has already been added to our website and/or social media profiles prior to the date of your consent withdrawal, Shedd Capital, LLC cannot guarantee or be responsible for the removal of that image from the independent control or archives of third parties who may have accessed or stored that image during the period of consent. By granting us consent, you accept this limitation. Shedd Capital, LLC will have no liability for any subsequent use of the image(s) by a third party after distribution occurred while your consent was valid and in effect.</div>
                    </li>
                    <li class="py-2">
                        <div class="font-bold text-gray-800">Representation:</div> 
                        <div>You affirm that you are the sole owner of the intellectual property rights to the photographs you are submitting, or that you have obtained all necessary permissions from the copyright holder to grant these rights to Shedd Capital, LLC. You also affirm that the photographs do not infringe upon the privacy rights, publicity rights, or any other rights of any person.</div>
                    </li>
                </ul>
            </div>
            <div v-else @click="expandConsentAgreement" class="p-4 bg-none flex w-full justify-between rounded-md border border-gray-300 text-gray-800 hover:underline hover:text-indigo-700 hover:bg-indigo-50 cursor-pointer">
                <h2 class="text-sm font-bold ">Photo Submission & Usage Agreement</h2>
                <chevron-down-mini-s-v-g class="mr-4" />
            </div>
            <div class="consent-section text-sm pt-4">
                <div class="w-full flex space-x-2 items-start pb-4">
                    <input @click="displayConsentForm" type="checkbox" id="userAgreementReadCheckbox" name="userAgreementReadCheckbox" required v-model="userAgreementRead" class="mt-1 accent-dark-secondary">
                    <label for="userAgreementReadCheckbox" class="">I have read, understood, and agree to the terms and conditions outlined above regarding the submission and use of my uploaded photographs.</label>
                </div>
                <!-- <p class="py-3">Please confirm your understanding and agreement by digitally signing below.</p> -->
                <div v-if="userAgreementRead" id="consentForm" class="h-full w-full flex flex-col justify-center items-center space-y-4 border-t border-gray-300 pt-4">
                    <div class="w-full flex flex-col space-y-4">
                        <div>Please indicate whether you consent Shedd Capital LLC to use your uploaded images as outlined in the Photo Submission & Usage Agreement located on this form.</div>
                        <div class="w-full flex space-x-2 pl-4 items-start">
                            <input type="radio" id="consentTrue" :value="true" v-model="userPhotoConsentData.consent" class="mt-1 accent-dark-secondary">
                            <label for="consentTrue" class="">I consent.</label>
                        </div>
                        <div class="w-full flex space-x-2 pl-4 items-start">
                            <input type="radio" id="consentFalse" :value="false" v-model="userPhotoConsentData.consent" class="mt-1 accent-dark-secondary">
                            <label for="consentFalse" class="">I do not consent.</label>
                        </div>
                        <div class="flex w-full space-x-4 pb-2">
                            <div class="w-full flex flex-col space-y-2">
                                <label for="signedName" class="">Full Name (signature):</label>
                                <input name="signedName" class="p-3 w-full border border-gray-300 rounded-md" required type="text" placeholder="John M. Smith"  v-model="userPhotoConsentData.signature">
                            </div>
                            <div class="w-full flex flex-col space-y-2">
                                <label for="signedDate" class="">Date:</label>
                                <div class="p-3 w-full border border-gray-300 bg-gray-150 rounded-md"> {{ formatDate(userPhotoConsentData.signed_date) }}</div>
                                <!-- <input name="signedDate" class="p-3 w-full border border-gray-300 rounded-md" required type="date" disabled v-model="userPhotoConsentData.signed_date"> -->
                            </div>
                        </div>

                    </div>
                    <!-- <button type="submit" class="bg-dark-primary px-4 py-3 rounded-md text-white hover:bg-dark-secondary" >Submit Consent</button> -->
                    <div class="w-full flex flex-col pt-6 space-y-4 border-t border-gray-300">
                        <button 
                            type="submit"
                            :disabled="!validForm"
                            class="px-4 py-3 bg-dark-primary text-white font-semibold leading-tight rounded-md transition duration-150 ease-in-out w-full"
                            :class="{ 'hover:bg-[#181B4E] hover:shadow-md focus:shadow-lg focus:outline-none focus:ring-0 active:shadow-lg' : validForm, 'opacity-50' : !validForm}"
                        >
                            Submit
                        </button>
                        <p class="text-xs">By clicking "Submit," you are providing your digital signature and agreeing to the terms outlined in this form. Your IP address, submission timestamp, and location data* will be recorded for verification purposes.</p>
                        <p class="text-xs">* If allowed and enabled in browser.</p>
                    </div>
                </div>
            </div>
        </form>
        <div v-else class="space-y-4">
            <div v-if="visibleUserConsentLogs" class="flex flex-col w-full">
                <div class="flex w-full items-center justify-between pb-2 ">
                    <div class="w-full flex justify-start items-center px-2">Submission Date:</div>
                    <div class="w-full flex justify-start items-center px-2">Signed Name:</div>
                    <div class="w-full flex justify-start items-center px-2">Consent Type:</div>
                </div>
                <div class="w-full">
                    <div v-for="userConsentLog in userConsentLogs" :key="userConsentLog.id" :class="getConsentStyle(userConsentLog)" class="flex w-full items-center border-b border-x justify-between py-2 first:rounded-t-md first:border-t last:rounded-b-md">
                        <div class="w-full flex justify-start items-center px-2">{{ formatDate(userConsentLog.created_at) }}</div>
                        <div class="w-full flex justify-start items-center px-2">{{ userConsentLog.signature }}</div>
                        <div class="w-full flex justify-start items-center px-2">{{ userConsentLog.consent ? 'I consent' : 'I withdraw consent' }}</div>
                    </div>
                </div>
            </div>
            <div v-else-if="currentUserConsent">
                <div  class="border border-gray-300 p-4 rounded-md cursor-pointer"  @click="showUserConsentLogs" :class="getConsentStyle(currentUserConsent)">
                    <div v-if="currentUserConsent.consent">
                        <div>You have consented to Shedd Capital's use of your uploaded photos.</div>
                    </div>
                    <div v-else>
                        <div>You have withdrawn your consent for Shedd Capital's use of your uploaded photos.</div>
                    </div>
                    <div class="flex justify-between items-cente pt-4">
                        <div>Submitted: {{ formatDate(currentUserConsent.created_at) }}</div>
                        <button class="hover:underline">View Consent History</button>
                    </div>
                </div>

            </div>
            <div v-else  class="p-4 rounded-md space-y-2 bg-amber-100 border border-amber-300 text-amber-800" >
                <div>You have not confirmed your consent preferences for Shedd Capital's use of your uploaded photos.</div>
            </div>
            <div @click="showConsentForm" class="cursor-pointer hover:underline">
                Click here to update consent preferences.
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, reactive, computed } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import ChevronUpMiniSVG from '../svg-components/ChevronUpMiniSVG.vue';
import ChevronDownMiniSVG from '../svg-components/ChevronDownMiniSVG.vue';
import { getTodaysDateAsString, formatDate } from '@/composables/formatDate';
import { createUserConsentLog } from '@/services/UserConsentLog';
import LoadingSpinner from '../base-components/LoadingSpinner.vue';


const store = useStore()
const $toast = useToast()

const props = defineProps([
    'isLoading', 
    'visibleConsentForm', 
    'userConsentLogs',
    'currentUserConsent',
])

const emit = defineEmits([
    'show-consent-form',
    'hide-consent-form',
    'get-user-consent-logs',
    'set-loading-true',
    'set-loading-false'
])

// const visibleConsentForm = ref(false)
const visibleUserConsentLogs = ref(false)

// const isLoading = ref(false)

const expandedConsentAgreement = ref(true)

const userAgreementRead = ref(false)

// const userConsentLogs = ref([]);

// const currentUserConsent = ref(null);

const userPhotoConsentData = reactive({
    consent: null,
    signature: null,
    signed_date: getTodaysDateAsString(),
    signed_user_lat: null,
    signed_user_long: null,
})

const validForm = computed(() => {
    if(userPhotoConsentData.consent != null && userPhotoConsentData.signature && userPhotoConsentData.signed_date) {
        return true
    }
    return false
})

function resetUserPhotoConsentDate() {
    userPhotoConsentData.consent = null
    userPhotoConsentData.signature = null
    userPhotoConsentData.signed_user_lat = null
    userPhotoConsentData.signed_user_long = null
    userPhotoConsentData.signed_date = getTodaysDateAsString()
}

function showConsentForm() {
    emit('show-consent-form')
    // visibleConsentForm.value = true
}

function hideConsentForm() {
    emit('hide-consent-form')
    // visibleConsentForm.value = false
}

function showUserConsentLogs() {
    visibleUserConsentLogs.value = true
}

function hideUserConsentLogs() {
    visibleUserConsentLogs.value = false
}

function expandConsentAgreement() {
    expandedConsentAgreement.value = true
}

function collapseConsentAgreement() {
    expandedConsentAgreement.value = false
}

function fetchLocationData() {
    let lat, long;
    const options = {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 0
    }
    return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const coordinates = pos.coords;
                lat = coordinates.latitude
                long = coordinates.longitude
                resolve({lat, long})
            },
            (err) => {
                console.warn(`ERROR(${err.code}): ${err.message}`);
                reject({lat, long})
            },
            options
        )
    })
}

function displayConsentForm() {
    setTimeout(() => {
        if(userAgreementRead.value) {
            collapseConsentAgreement()
            scrollToSection('consentForm')
        } else {
            expandConsentAgreement()
        }
    }, 50)
}

function scrollToSection(sectionId) {
  const section = document.getElementById(sectionId);
  const yOffset = -70;
  const y = section.getBoundingClientRect().top + window.scrollY + yOffset;
  window.scrollTo({ top: y, behavior: "smooth" });
}

function getConsentStyle(userConsentLog) {
    if(userConsentLog.consent) {
        return 'bg-emerald-100 border-emerald-300 text-emerald-800'
    } else {
        return 'bg-orange-100 border-orange-300 text-orange-800'
    }
}

function loadUserConsentLogs() {
    emit('get-user-consent-logs')
}

function setLoadingTrue() {
    emit('set-loading-true')
}

function setLoadingFalse() {
    emit('set-loading-false')
}

async function submitUserConsent() {
    setLoadingTrue()
    const token = store.state.auth.token
    const response = await createUserConsentLog(token, userPhotoConsentData)
    if(response.status == 201) {
        $toast.open({
            type: 'success',
            message: 'Submitted user consent',
            duration: 5000
        })
        resetUserPhotoConsentDate()
        hideConsentForm()
        hideUserConsentLogs()
        loadUserConsentLogs()
    } else {
       $toast.open({
            type: 'error',
            message: 'Error submitting user consent',
            duration: 10000
       })
    }
    setLoadingFalse()
}

async function submitConsent() {
    console.log('submitConsent')

    fetchLocationData().then((data) => {
        userPhotoConsentData.signed_user_lat = data.lat
        userPhotoConsentData.signed_user_long = data.long
        console.log(userPhotoConsentData)
    }).catch((err) => {
        console.error(err)
    }).finally(() => {
        submitUserConsent()
    })
}

// async function loadUserConsentLogs() {
//     isLoading.value = true
//     const token = store.state.auth.token
//     const userId = store.state.auth.user.id
//     const response = await getUserConsentLogsByUserId(token, userId)
//     if(response.status == 200) {
//         userConsentLogs.value = response.data
//         if(userConsentLogs.value.length > 0) {
//             currentUserConsent.value = userConsentLogs.value[0]
//         } else {
//             visibleConsentForm.value = true
//         }
//     }
//     isLoading.value = false
// }

// loadUserConsentLogs()
</script>